<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Grades"
      subtitle="Staff grading levels"
      icon="pi-chart-bar"
    >
      <template #actions>
        <Button
          label="Add"
          icon="pi pi-plus"
          size="small"
          :disabled="mutating"
          @click="openCreate"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadItems"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div v-if="loading && !items.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadItems" />
        </div>
      </Message>

      <template v-else>
        <section class="settings-grades-section__quick-add" aria-label="Quick add grade">
          <div class="settings-grades-section__quick-add-title">Quick add</div>
          <form class="settings-grades-section__quick-add-form" @submit.prevent="handleQuickAdd">
            <FloatLabel variant="on" class="settings-grades-section__quick-add-name">
              <InputText
                id="grade-quick-name"
                v-model="quickAdd.name"
                maxlength="120"
                autocomplete="off"
                :disabled="creating"
                class="w-full rounded-xl"
              />
              <label for="grade-quick-name" class="text-sm font-medium">
                Grade Name <span class="text-red-500">*</span>
              </label>
            </FloatLabel>
            <Button
              type="submit"
              label="Add"
              icon="pi pi-plus"
              :loading="creating"
              :disabled="!quickAdd.name?.trim()"
            />
          </form>
        </section>

        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="grades-show-inactive"
          search-placeholder="Search grades..."
          :loading="loading"
        />

        <div v-if="!filteredItems.length" class="settings-org-section__empty">
          <i class="pi pi-chart-bar settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredItems"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable style="min-width: 10rem">
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column field="level" header="Level" sortable style="width: 5rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">{{ data.level }}</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="employees_count" header="Employees" sortable style="width: 6rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.employees_count ?? 0 }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <GradeEditModal
      v-model:visible="editModalVisible"
      :grade="selectedItem"
      :saving="mutating"
      @save="handleSave"
      @delete="handleDelete"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import GradeEditModal from '../modals/GradeEditModal.vue';
import { useSettingsEntityList } from '../../composables/useSettingsEntityList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import {
  createGrade,
  deleteGrade,
  fetchGradesList,
  updateGrade,
  updateGradeStatus,
} from '@/api/gradesApi';
import { useClientTableSort } from '@/utils/tableSort';

defineOptions({ name: 'SettingsGradesSection' });

const { sortField, sortOrder, onSort } = useClientTableSort('level');

defineProps({
  section: { type: Object, default: null },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { showInactive, afterMutation } = useSettingsEntityList();

const items = ref([]);
const loading = ref(false);
const error = ref('');
const mutating = ref(false);
const creating = ref(false);
const searchQuery = ref('');
const editModalVisible = ref(false);
const selectedItem = ref(null);
const quickAdd = reactive({ name: '' });

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? items.value.filter((item) => {
        const haystack = [
          item.name,
          item.code,
          String(item.level),
          String(item.id),
          isOrgReferenceActive(item) ? 'active' : 'inactive',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
    : items.value;
  return sortOrgReferenceRows(base, { sortBy: 'level' });
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(items.value, 'grades', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No grades match your search.';
  if (showInactive.value) return 'No grades found. Use Quick add or Add to create one.';
  return 'No active grades yet. Use Quick add or Add to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const notifySuccess = (detail) => {
  toast.add({ severity: 'success', summary: 'Success', detail, life: 2500 });
};

const notifyError = (err, fallback) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: err?.message || fallback,
    life: 3500,
  });
};

const afterMutationReload = () => afterMutation(loadItems);

const loadItems = async () => {
  loading.value = true;
  error.value = '';
  try {
    const list = await fetchGradesList({ includeInactive: showInactive.value });
    items.value = sortOrgReferenceRows(Array.isArray(list) ? list : [], { sortBy: 'level' });
  } catch (err) {
    error.value = err?.message || 'Failed to load grades.';
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  selectedItem.value = null;
  editModalVisible.value = true;
};

const openEdit = (item) => {
  selectedItem.value = item;
  editModalVisible.value = true;
};

const handleQuickAdd = async () => {
  const name = quickAdd.name?.trim();
  if (!name || creating.value) return;
  creating.value = true;
  try {
    await createGrade({ name });
    quickAdd.name = '';
    notifySuccess('Grade created');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to create grade.');
  } finally {
    creating.value = false;
  }
};

const handleSave = async (payload) => {
  mutating.value = true;
  try {
    if (payload.id) {
      await updateGrade({
        grade_id: payload.id,
        name: payload.name?.trim(),
        level: payload.level,
        is_active: payload.is_active ? 1 : 0,
      });
      notifySuccess('Grade updated');
    } else {
      await createGrade({ name: payload.name?.trim() });
      notifySuccess('Grade created');
    }
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to save grade.');
  } finally {
    mutating.value = false;
  }
};

const handleDelete = async ({ id }) => {
  mutating.value = true;
  try {
    await deleteGrade({ grade_id: id });
    notifySuccess('Grade deleted');
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to delete grade.');
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (item) => {
  confirm.require({
    header: 'Deactivate grade',
    message: `"${item.name}" will be hidden from profile options. Assigned employees keep their grade.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateItem(item),
  });
};

const deactivateItem = async (item) => {
  mutating.value = true;
  try {
    await updateGradeStatus({
      grade_id: item.id,
      is_active: false,
    });
    notifySuccess('Grade deactivated');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to deactivate grade.');
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (item) => {
  confirm.require({
    header: 'Delete grade',
    message: `Delete "${item.name}"? This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => handleDelete({ id: item.id }),
  });
};

onMounted(() => {
  loadItems();
});

watch(showInactive, () => {
  loadItems();
});
</script>

<style scoped>
.settings-org-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-org-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-org-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-org-section__message {
  margin: 0;
}

.settings-grades-section__quick-add {
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  padding: 1rem;
  background: var(--crc-panel-bg);
}

.settings-grades-section__quick-add-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--crc-text-muted, var(--text-color-secondary));
  margin-bottom: 0.75rem;
}

.settings-grades-section__quick-add-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
}

.settings-grades-section__quick-add-name {
  flex: 1 1 14rem;
  min-width: 12rem;
}

.settings-org-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-org-section__empty-icon {
  font-size: 1.5rem;
}

.settings-org-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.65;
}
</style>
